/**
 * "We have this" (owner, 2026-09-30): "All these types of questions: when do you have to throw them away, or sell them
 * by. It should be very easy to figure out what you want to do with it later. In the library you should basically be
 * able to declare that you have something, like croissants, on the page, and actually place it in storage as an intent
 * at the end of the article. Even if we don't know that it's in the fridge or that the place has it… So you can easily
 * turn on the application and start from any state of your business, and slowly get better and better track of
 * everything."
 *
 * A reader of a how-long entry or a library guide says "we have this", answers one question (where is it?), and the
 * place now has an INTENT: "This place has croissants, kept in the display case", even if nobody has checked. The item
 * itself is made in the model that already owns that kind of thing (food → the food store, supply → the supplies list,
 * equipment → things); this file holds what only the intent knows: that we were TOLD (not checked), where, and when
 * to ask again.
 *
 * Gates (tests in test/haveit.test.ts; the site's side in apps/landing/test/have.test.ts):
 * | H1 Told is not checked | status "told" until a person answers "Still have it? Where?" or does something with it
 *   (a food act). The two are never merged, and a told item carries no date, so it can never raise a "throw out" it
 *   cannot know about |
 * | H2 Not sure is an answer | "Not sure yet" saves the intent like any other place |
 * | H3 Never lost, never a wall | a bad or missing field in the link keeps the rest (the item alone is enough); an
 *   unknown item is dropped quietly; nothing here blocks a sign-up or any safety screen |
 * | H4 One ask a day, never on a safety gate | reaskDue() hands back at most one intent, only when no safety gate is
 *   open, only once per local day, never on the day it was told |
 * | H5 The rule is sourced | every rule line is a sentence from a sourced guide (supplies, equipment) or carries the
 *   Food Code sections it rests on (food); the site's tests check its numbers against the quoted Code |
 */
import type { FoodPlace, FoodRecord } from "./foodstore.ts";
import { newFood } from "./foodstore.ts";
import type { AskAnswer } from "./ask.ts";
import type { IsoDate } from "./model.ts";
import { LIBRARY } from "./library.ts";

export type HaveKind = "food" | "supply" | "equipment";
export const HAVE_KINDS: readonly HaveKind[] = ["food", "supply", "equipment"];

/** One answer to "Where is it?". `in` finishes a sentence: "kept in the display case". */
export interface HaveWhere { key: string; label: string; in: string; /** Food only: the food store's place. */ place?: FoodPlace }

export const HAVE_WHERE: Record<HaveKind, HaveWhere[]> = {
  food: [
    { key: "fridge", label: "Fridge", in: "in the fridge", place: "fridge" },
    { key: "freezer", label: "Freezer", in: "in the freezer", place: "freezer" },
    { key: "display", label: "Display case or counter", in: "in the display case", place: "counter" },
    { key: "dry", label: "Dry storage", in: "in dry storage", place: "pantry" },
    { key: "unsure", label: "Not sure yet", in: "(we don't know where yet)", place: "counter" },
  ],
  supply: [
    { key: "dry", label: "Dry storage", in: "in dry storage" },
    { key: "station", label: "At the station where it's used", in: "at the station where it's used" },
    { key: "unsure", label: "Not sure yet", in: "(we don't know where yet)" },
  ],
  equipment: [
    { key: "kitchen", label: "In the kitchen", in: "in the kitchen" },
    { key: "front", label: "Front counter or dining room", in: "at the front counter or dining room" },
    { key: "back", label: "Back room or storage", in: "in the back room" },
    { key: "unsure", label: "Not sure yet", in: "(we don't know where yet)" },
  ],
};
export const haveWhere = (kind: HaveKind, key: string | undefined): HaveWhere | undefined => HAVE_WHERE[kind].find((w) => w.key === key);

/** A question and its sourced answer, for the in-app Ask with this item as context. */
export interface HaveAsk { q: string; /** Words that also count as asking this. */ also: string[]; answer: string; items: { title: string; lines: string[] }[] }

export interface IntentItem {
  key: string;
  kind: HaveKind;
  /** Title case: "Croissants (plain)". */
  name: string;
  /** In a sentence: "croissants". */
  say: string;
  /** The rule in one glance, in plain words. Numbers in it must be in the sources named by `cites`. */
  rule: string;
  /** Food Code sections (or the guide's own citations) the rule rests on. */
  cites: string[];
  /** Food: what the food store calls it. */
  foodName?: string;
  /** Supply: a key in actions.ts SUPPLY_SEEDS. */
  supplyKey?: string;
  /** Equipment: an id in objectops-data.ts OBJECT_TYPES. */
  typeId?: string;
  /** The page that hosts the "We have this" for it. */
  page?: string;
  ask?: HaveAsk[];
}

/* ─────────────── the rules, as plain sentences (food) ─────────────── */

const CROISSANT_ASK: HaveAsk[] = [
  {
    q: "Can delivered croissants sit under glass on display?",
    also: ["pastries", "pastry", "glass", "dome", "case", "open", "air", "display", "covered", "delivered", "bakery", "counter", "time", "limit", "croissant", "croissants", "sit", "like"],
    answer: "Yes, plain croissants can sit covered under glass on display. They are not TCS food, so the Food Code sets no time limit for them.",
    items: [
      { title: "Why it is allowed", lines: [
        "Plain croissants are not TCS food, so there is no Food Code time limit (1-201.10(B)).",
        "Glass counts. A glass dome or a closed display case meets 3-306.11: food on display is protected by display cases or other effective means.",
        "They must come from an approved, licensed bakery (3-201.11).",
      ] },
      { title: "Filled pastries are different", lines: [
        "Cream, custard or cheese filling makes a pastry TCS food: keep it at 41°F or below (3-501.16).",
        "Or use the 4-hour mark: write the time 4 hours ahead and toss at that time. That needs a written procedure kept in the shop (3-501.19).",
      ] },
    ],
  },
  {
    q: "How long can croissants sit out?",
    also: ["croissant", "croissants", "plain", "counter", "limit", "hours", "overnight", "stale", "throw", "toss", "sell", "pastries"],
    answer: "Plain croissants have no Food Code time limit, so how long they keep is a quality call. Keep them covered on display.",
    items: [
      { title: "The rule", lines: [
        "Plain croissants are not TCS food, so the Food Code sets no time limit (1-201.10(B)).",
        "Cover them on display: a dome, a closed case or packaging (3-306.11).",
        "Filled ones (cream, custard, cheese): 41°F or below, or the 4-hour mark with a written procedure (3-501.16, 3-501.19).",
      ] },
    ],
  },
];
const FILLED_ASK: HaveAsk[] = [
  {
    q: "Can cream-filled pastries sit out on display?",
    also: ["filled", "cream", "custard", "cheese", "pastries", "pastry", "eclair", "sit", "out", "display", "glass", "counter", "hours", "fridge", "cold"],
    answer: "Only for 4 hours, with a written plan and a time mark. Otherwise keep filled pastries at 41°F or below.",
    items: [
      { title: "The rule", lines: [
        "Cream, custard and cheese fillings make a pastry TCS food: keep it at 41°F or below (3-501.16).",
        "On display without cold: use the 4-hour mark. Take it out at 41°F or below, write the time 4 hours later, and serve or toss by then. This needs a written procedure kept in the shop (3-501.19).",
        "A filled pastry with no mark, or past its mark, is tossed (3-501.19).",
      ] },
    ],
  },
];

/** Plain sentences, each carrying the exception it needs (the owner's rule for one-tap lines). */
const FOOD_ITEMS: IntentItem[] = [
  { key: "croissant", kind: "food", name: "Croissants (plain)", say: "croissants", foodName: "Croissants", page: "/how-long/pastries/", cites: ["3-306.11", "3-501.16(A)(2)", "3-501.19(B)"], ask: CROISSANT_ASK,
    rule: "Croissants (plain): no Code time limit. Cover them on display. Filled ones: 41°F or below, or toss after 4 hours out on a written 4-hour plan." },
  { key: "filled-pastry", kind: "food", name: "Filled pastries (cream, custard, cheese)", say: "filled pastries", foodName: "Filled pastries", page: "/how-long/pastries/", cites: ["1-201.10(B)", "3-501.16(A)(2)", "3-501.19(B)"], ask: FILLED_ASK,
    rule: "Filled pastries (cream, custard, cheese): 41°F or below. Or, on a written 4-hour plan, mark the time 4 hours out and toss at that time." },
  { key: "cheesecake", kind: "food", name: "Cheesecake", say: "cheesecake", foodName: "Cheesecake", page: "/how-long/cheesecake/", cites: ["3-501.16(A)(2)", "3-501.17(A)"],
    rule: "Cheesecake: 41°F or below. Made here and kept over 24 hours: 7 days at most, the day made is day 1." },
  { key: "cold-brew", kind: "food", name: "Cold brew", say: "cold brew", foodName: "Cold brew", page: "/how-long/cold-brew/", cites: ["1-201.10(B)", "3-501.16(A)(2)", "3-501.17(A)"],
    rule: "Cold brew: the Code names no number for it. Until your county or a lab test says otherwise, treat it as TCS: 41°F or below, 7 days at most." },
  { key: "milk", kind: "food", name: "Milk, once opened", say: "milk", foodName: "Milk", page: "/how-long/milk/", cites: ["3-501.16(A)(2)", "3-501.17(A)", "3-501.17(B)"],
    rule: "Milk: 41°F or below. Once opened, 7 days at most, the day opened is day 1, never past the dairy's use-by date." },
  { key: "half-and-half", kind: "food", name: "Half-and-half and cream", say: "half-and-half", foodName: "Half-and-half", page: "/how-long/half-and-half/", cites: ["3-501.16(A)(2)", "3-501.17(A)", "3-501.17(B)"],
    rule: "Half-and-half and cream: 41°F or below. Once opened, 7 days at most, the day opened is day 1, never past the dairy's use-by date." },
  { key: "whipped-cream", kind: "food", name: "Whipped cream", say: "whipped cream", foodName: "Whipped cream", page: "/how-long/whipped-cream/", cites: ["3-501.16(A)(2)", "3-501.17(A)"],
    rule: "Whipped cream: 41°F or below, between drinks too. Made here and kept over 24 hours: 7 days at most, the day made is day 1." },
  { key: "eggs", kind: "food", name: "Raw eggs in the shell", say: "eggs", foodName: "Eggs", page: "/how-long/eggs/", cites: ["3-501.16(B)", "1-201.10(B)", "3-501.17(A)"],
    rule: "Raw eggs in the shell: a fridge that holds 45°F or below. The 7-day date doesn't apply to them; follow the carton's date." },
  { key: "hard-boiled-eggs", kind: "food", name: "Hard-boiled eggs", say: "hard-boiled eggs", foodName: "Hard-boiled eggs", page: "/how-long/hard-boiled-eggs/", cites: ["3-501.16(A)(2)", "3-501.17(A)", "1-201.10(B)"],
    rule: "Hard-boiled eggs, peeled or cooled in water: 41°F or below, 7 days at most, the day cooked is day 1. Cooled in air with the shell whole, they are not TCS." },
  { key: "yogurt", kind: "food", name: "Yogurt and parfaits", say: "yogurt", foodName: "Yogurt", page: "/how-long/yogurt/", cites: ["3-501.16(A)(2)", "3-501.17(A)", "3-501.17(G)(4)"],
    rule: "Yogurt: 41°F or below. A plant-made tub needs no 7-day date; follow the maker's date. A parfait you build: 7 days at most, the day made is day 1." },
  { key: "cut-fruit", kind: "food", name: "Cut fruit and melon", say: "cut fruit", foodName: "Cut fruit", page: "/how-long/cut-fruit/", cites: ["1-201.10(B)", "3-501.16(A)(2)", "3-501.17(A)"],
    rule: "Cut melon: 41°F or below, 7 days at most, the day cut is day 1. Treat a fruit cup with melon in it the same way." },
  { key: "sandwiches", kind: "food", name: "Sandwiches and wraps", say: "sandwiches", foodName: "Sandwiches", page: "/how-long/sandwiches/", cites: ["3-501.16(A)(2)", "3-501.17(A)", "3-501.17(C)"],
    rule: "Sandwiches with meat, egg or cheese: 41°F or below, 7 days at most, counted from the oldest dated filling." },
  { key: "deli-meat", kind: "food", name: "Deli meat", say: "deli meat", foodName: "Deli meat", page: "/how-long/deli-meat/", cites: ["3-501.16(A)(2)", "3-501.17(A)", "3-501.17(B)", "3-501.17(G)(6)-(7)"],
    rule: "Deli meat: 41°F or below. Once opened, 7 days at most, the day opened is day 1, never past the maker's use-by date. Shelf-stable dry sausage and salt-cured ham need no date." },
  { key: "cooked-rice", kind: "food", name: "Cooked rice", say: "cooked rice", foodName: "Cooked rice", page: "/how-long/cooked-rice/", cites: ["3-501.14(A)", "3-501.16(A)(1)", "3-501.16(A)(2)", "3-501.17(A)"],
    rule: "Cooked rice: hold it at 135°F or above, or 41°F or below. Cool it from 135°F to 70°F within 2 hours, then to 41°F within 6 hours in all. Kept cold, 7 days at most." },
  { key: "soup", kind: "food", name: "Soup", say: "soup", foodName: "Soup", page: "/how-long/soup/", cites: ["3-501.14(A)", "3-501.16(A)(1)", "3-501.16(A)(2)", "3-501.17(A)"],
    rule: "Soup: hold it at 135°F or above, or 41°F or below. Cool leftovers from 135°F to 70°F within 2 hours, then to 41°F within 6 hours in all. Kept cold, 7 days at most." },
];

/* ─────────────── supplies and equipment: the rule is a sentence from the guide that names them ─────────────── */

interface GuideLine { guide: string; heading: string; starts: string }
/** The sentence in a library guide (and the sources that section cites), found by its first words. Throws if the guide changed. */
export function guideLine(g: GuideLine): { text: string; cites: string[] } {
  const topic = LIBRARY.find((t) => t.id === g.guide);
  const sec = topic?.sections.find((s) => s.heading === g.heading);
  const raw = sec?.body.split("\n").map((l) => l.replace(/^[-\d.]+\s+/, "").trim()).find((l) => l.startsWith(g.starts));
  if (!topic || !sec || !raw) throw new Error(`haveit: the line "${g.starts}" is not in library guide ${g.guide} › ${g.heading}`);
  return { text: raw, cites: sec.citations };
}
const fromGuide = (key: string, kind: "supply" | "equipment", name: string, say: string, ref: { supplyKey?: string; typeId?: string }, g: GuideLine): IntentItem => {
  const l = guideLine(g);
  return { key, kind, name, say, ...ref, page: `/library/${g.guide}/`, rule: l.text, cites: l.cites };
};
const SUPPLY_EQUIPMENT_ITEMS: IntentItem[] = [
  fromGuide("sanitizer", "supply", "Food-contact sanitizer", "sanitizer", { supplyKey: "sanitizer" }, { guide: "cleaning-sanitizing", heading: "Use the right product", starts: "Use only EPA-registered sanitizers" }),
  fromGuide("test-strips", "supply", "Sanitizer test strips", "sanitizer test strips", { supplyKey: "test-strips" }, { guide: "cleaning-sanitizing", heading: "Mixing and testing", starts: "Test with the matching strip" }),
  fromGuide("thermometer", "supply", "Probe thermometer", "a probe thermometer", { supplyKey: "thermometer" }, { guide: "thermometers", heading: "What you need", starts: "A food thermometer accurate" }),
  fromGuide("gloves", "supply", "Single-use gloves", "single-use gloves", { supplyKey: "gloves" }, { guide: "gloves-bare-hands", heading: "Using gloves the right way", starts: "Use nitrile or vinyl gloves" }),
  fromGuide("hand-soap", "supply", "Hand soap", "hand soap", { supplyKey: "hand-soap" }, { guide: "handwashing", heading: "Soap and hand sanitizer", starts: "Plain soap is enough" }),
  fromGuide("wiping-cloths", "supply", "Wiping cloths", "wiping cloths", { supplyKey: "wiping-cloths" }, { guide: "wiping-cloths", heading: "Keep cloths apart", starts: "Dry cloths for wiping spills" }),
  fromGuide("restroom-mop", "supply", "Restroom mop, bucket and cloths", "a restroom mop", { supplyKey: "restroom-mop" }, { guide: "restroom-mops-rags", heading: "Keep two separate sets", starts: "One labeled set of cloths" }),
  fromGuide("three-compartment-sink", "equipment", "Three-compartment sink", "a three-compartment sink", { typeId: "three-compartment-sink" }, { guide: "three-compartment-sink", heading: "Sink rules", starts: "A sink with at least 3 compartments" }),
  fromGuide("dish-machine-high-temp", "equipment", "Dish machine (hot water sanitizing)", "a hot-water dish machine", { typeId: "dish-machine-high-temp" }, { guide: "dishwashers", heading: "If it isn't working", starts: "If the temperature or sanitizer is low" }),
  fromGuide("dish-machine-low-temp", "equipment", "Dish machine (chemical sanitizing)", "a chemical dish machine", { typeId: "dish-machine-low-temp" }, { guide: "dishwashers", heading: "If it isn't working", starts: "If the temperature or sanitizer is low" }),
  fromGuide("sanitizer-bucket", "equipment", "Sanitizer bucket and wiping cloths", "a sanitizer bucket", { typeId: "sanitizer-bucket" }, { guide: "wiping-cloths", heading: "Keep the bucket strong", starts: "Wet cloths sit in sanitizer" }),
  fromGuide("hand-sink", "equipment", "Handwashing sink", "a handwashing sink", { typeId: "hand-sink" }, { guide: "handwashing", heading: "Hand sinks are for hands only", starts: "Keep it stocked with soap" }),
  fromGuide("reach-in-fridge", "equipment", "Reach-in refrigerator", "a reach-in fridge", { typeId: "reach-in-fridge" }, { guide: "hot-cold-holding", heading: "The limits", starts: "Cold food: 41°F or below" }),
  fromGuide("walk-in", "equipment", "Walk-in cooler", "a walk-in cooler", { typeId: "walk-in" }, { guide: "hot-cold-holding", heading: "The limits", starts: "Cold food: 41°F or below" }),
  fromGuide("steam-table", "equipment", "Steam table or hot well", "a steam table", { typeId: "steam-table" }, { guide: "hot-cold-holding", heading: "Check it, don't guess", starts: "Steam tables, chafing dishes" }),
  fromGuide("bait-station", "equipment", "Pest bait stations", "pest bait stations", { typeId: "bait-station" }, { guide: "pest-prevention", heading: "Inspect on a schedule", starts: "A licensed pest control operator" }),
];

export const INTENT_ITEMS: IntentItem[] = [...FOOD_ITEMS, ...SUPPLY_EQUIPMENT_ITEMS];
export const intentItem = (key: string | undefined | null): IntentItem | undefined => (key ? INTENT_ITEMS.find((i) => i.key === key) : undefined);

/** The library guides that name a supply or an equipment item, and which ones (their "We have this" at the end). */
export const LIBRARY_HAVE: Record<string, string[]> = {
  handwashing: ["hand-soap", "hand-sink"],
  "gloves-bare-hands": ["gloves"],
  thermometers: ["thermometer"],
  "cleaning-sanitizing": ["sanitizer", "test-strips"],
  "three-compartment-sink": ["three-compartment-sink", "test-strips"],
  dishwashers: ["dish-machine-high-temp", "dish-machine-low-temp"],
  "wiping-cloths": ["sanitizer-bucket", "wiping-cloths"],
  "restroom-mops-rags": ["restroom-mop"],
  "hot-cold-holding": ["reach-in-fridge", "walk-in", "steam-table"],
  "fridge-storage-order": ["reach-in-fridge", "walk-in"],
  "pest-prevention": ["bait-station"],
};

/* ─────────────── the link: ?have=croissant&where=display ─────────────── */

/** What the reader said. Everything but `key` is optional (H3: a missing or bad extra never loses the item). */
export interface HaveInput { key: string; where?: string; amount?: string; cameOn?: IsoDate }

export const HAVE_AMOUNT_MAX = 40;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const dayNum = (d: string) => Math.round(Date.parse(`${d}T12:00:00Z`) / 86_400_000);

/** The amount as typed: trimmed, control characters out, at most 40 characters. */
export function cleanAmount(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const s = v.replace(/[\u0000-\u001f\u007f<>]/g, " ").replace(/\s+/g, " ").trim().slice(0, HAVE_AMOUNT_MAX).trim();
  return s || undefined;
}
/** A day it came in or was made: a real date, not in the future (a day of slack for time zones), within the last year. */
export function cleanCameOn(v: unknown, today?: IsoDate): IsoDate | undefined {
  if (typeof v !== "string" || !DATE_RE.test(v) || Number.isNaN(Date.parse(`${v}T12:00:00Z`)) || new Date(`${v}T12:00:00Z`).toISOString().slice(0, 10) !== v) return undefined;
  if (today) { const d = dayNum(today) - dayNum(v); if (d < -1 || d > 366) return undefined; }
  return v;
}
/** Reads the link's query (`?have=croissant&where=display&amt=…&on=2026-09-30`). Unknown item → nothing; a bad extra is dropped. */
export function parseHaveParams(search: string | URLSearchParams): HaveInput | undefined {
  const p = typeof search === "string" ? new URLSearchParams(search) : search;
  const item = intentItem((p.get("have") ?? "").trim().toLowerCase());
  if (!item) return undefined;
  const where = (p.get("where") ?? "").trim().toLowerCase();
  const out: HaveInput = { key: item.key };
  if (haveWhere(item.kind, where)) out.where = where;
  const amount = cleanAmount(p.get("amt")); if (amount) out.amount = amount;
  const on = cleanCameOn(p.get("on")); if (on) out.cameOn = on;
  return out;
}
/** The query that carries an intent through a sign-up link. */
export function haveQuery(i: HaveInput): string {
  const p = new URLSearchParams({ have: i.key });
  if (i.where) p.set("where", i.where);
  if (i.amount) p.set("amt", i.amount);
  if (i.cameOn) p.set("on", i.cameOn);
  return p.toString();
}

/* ─────────────── the stored intent ─────────────── */

export type HaveStatus = "told" | "checked" | "gone";
export type HaveVia = "site" | "app" | "ask" | "import";

/** One intent as stored (migration 0059 `have_intents`). A home has no location. */
export interface StoredHave {
  id: string;
  orgId: string;
  locationId?: string;
  kind: HaveKind;
  key: string;
  name: string;
  /** Where it is kept: a key of HAVE_WHERE[kind] ("display"). Named `kept` because `where` is a SQL word. */
  kept: string;
  amount?: string;
  cameOn?: IsoDate;
  /** "told": a person said so and nobody checked. "checked": a person answered "Still have it? Where?" or used it. "gone": no longer here. */
  status: HaveStatus;
  via: HaveVia;
  toldAt: string;
  /** The place's local day it was told (so "not on the day it was told" is the place's day, not UTC's). */
  toldOn: IsoDate;
  toldBy: string;
  checkedAt?: string;
  checkedBy?: string;
  /** The last local day Today asked about it (H4). */
  askedOn?: IsoDate;
  /** The item this made: a food record id, a supply key, or a thing id. */
  refId?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

/** A sentence for the confirm screen: "You told us you have croissants in the display case." */
export function haveSentence(key: string, where?: string): string {
  const item = intentItem(key);
  if (!item) return "";
  const w = haveWhere(item.kind, where);
  return `You told us you have ${item.say}${w ? ` ${w.in}` : ""}.`;
}
/** The title line of an intent, "Croissants in the display case". */
export function haveTitle(key: string, where?: string): string {
  const item = intentItem(key);
  if (!item) return "";
  const w = haveWhere(item.kind, where);
  return `${item.name.replace(/ \(.*\)$/, "")}${w ? ` ${w.in}` : ""}`;
}
/** The next relevant thing for a new intent: one short question the person can answer standing there. */
export function haveNext(key: string, where?: string): { line: string; safety: boolean } {
  const item = intentItem(key);
  if (!item) return { line: "", safety: false };
  const t = haveTitle(key, where);
  if (item.key === "croissant") return where === "fridge" || where === "freezer" ? { line: `${t}: plain croissants don't need the cold. Covered on display is fine.`, safety: false } : { line: `${t}: covered? ✓`, safety: false };
  if (item.key === "filled-pastry") return { line: where === "fridge" ? `${t}: 41°F or below? Check the fridge.` : `${t}: kept cold, or on the 4-hour plan with a time mark?`, safety: true };
  if (item.kind === "food") {
    if (where === "fridge") return { line: `${t}: 41°F or below? Check the fridge.`, safety: true };
    if (where === "freezer") return { line: `${t}: write the day it went in.`, safety: false };
    if (where === "display") return { line: `${t}: covered, and cold enough if it needs the cold?`, safety: true };
    return { line: `${t}: write the day it came in or was made.`, safety: false };
  }
  return { line: `${t}: ${item.rule}`, safety: false };
}

/** A food record for a "we have this" food (no clocks, no dates: it was told, not checked). */
export function haveFoodRecord(item: IntentItem, where: string, id: string): FoodRecord {
  const w = haveWhere("food", where) ?? haveWhere("food", "unsure")!;
  const name = item.foodName ?? item.name;
  const base = newFood({ kind: "made", at: new Date(0).toISOString(), name }, id);
  return { ...base, id, name, place: w.place ?? "counter", label: [name], history: [] };
}

/** Mark an intent checked (H1). `where` is the new answer when they gave one; `still: false` = "we don't have it". */
export function checkHave(h: StoredHave, a: { where?: string; still?: boolean }, by: string, at: string): StoredHave {
  const kept = haveWhere(h.kind, a.where) ? a.where! : h.kept;
  if (a.still === false) return { ...h, status: "gone", checkedAt: at, checkedBy: by, updatedAt: at, updatedBy: by };
  return { ...h, kept, status: "checked", checkedAt: at, checkedBy: by, updatedAt: at, updatedBy: by };
}

/* ─────────────── the gentle re-ask (H4) ─────────────── */

/**
 * The one intent Today may ask about now, or nothing: still "told", not asked today, not told today, and no safety gate
 * open (an urgent finding, "Open for food?" not ready, a check-in gate). Oldest first.
 */
export function reaskDue(all: readonly StoredHave[], today: IsoDate, gate: boolean): StoredHave | undefined {
  if (gate) return undefined;
  if (all.some((h) => h.askedOn === today)) return undefined; // at most one ask a day, across all of them
  return all
    .filter((h) => h.status === "told" && h.toldOn < today)
    .sort((a, b) => a.toldAt.localeCompare(b.toldAt))[0];
}
/** Items said in the plural ("croissants"), so the ask reads "Where are they?" and not "Where is it?". */
const PLURAL = new Set(["croissant", "filled-pastry", "eggs", "hard-boiled-eggs", "sandwiches", "test-strips", "gloves", "wiping-cloths", "bait-station"]);
export const isPluralItem = (key: string) => PLURAL.has(key);
/** "Still have croissants? Where are they?" */
export const HAVE_ASK_LINE = (h: Pick<StoredHave, "key">) => `Still have ${intentItem(h.key)?.say ?? "it"}? Where ${PLURAL.has(h.key) ? "are they" : "is it"}?`;

/* ─────────────── Ask with the item as context ─────────────── */

const STOP = new Set("a an and are as at be by can could do does did for from get got had has have how i if in into is it its me my of on or our should so that the their them then there these this to too us was we what when where which who why will with would you your like".split(" "));
const words = (q: string) => [...new Set(q.toLowerCase().replace(/[’']/g, "").split(/[^a-z0-9°]+/).filter((w) => w.length > 1 && !STOP.has(w)))];
/** The question the "Ask about it" button asks for an item: its own first question, else a plain one its rule answers. */
export function haveQuestion(key: string): string {
  const item = intentItem(key);
  if (!item) return "";
  return item.ask?.[0]?.q ?? (item.kind === "food" ? `How long is ${item.say} good for?` : `How do we keep ${item.say}?`);
}
const HANDLING = new Set("long cover covered keep kept store stored sit sitting out throw toss sell serve fridge cold display glass counter expire expires date label good safe hold temperature hours days".split(" "));

function toAnswer(item: IntentItem, a: { answer: string; items: { title: string; lines: string[] }[] }): AskAnswer {
  return {
    answer: a.answer, steps: a.items.flatMap((s) => s.lines), fixes: [],
    sections: a.items.map((s) => ({ title: s.title, items: s.lines })),
    sources: item.cites.slice(0, 4).map((c) => ({ kind: "requirement" as const, id: `have:${item.key}:${c}`, title: `${item.name}: Food Code ${c}`, citation: `Food Code ${c}` })),
    grounded: true, engine: "catalog",
  };
}
/**
 * The sourced answer when the question is about an item we have a rule for. With `haveKey` (asked from that item's
 * flow) a handling question gets that item's rule; without it, only a question that matches one of the item's own
 * questions closely does. Never a guess: no match → undefined and the normal Ask runs.
 */
export function haveAnswer(question: string, haveKey?: string): AskAnswer | undefined {
  const qw = words(question);
  if (!qw.length) return undefined;
  const ctxItem = intentItem(haveKey);
  const pool = ctxItem ? [ctxItem, ...INTENT_ITEMS.filter((i) => i !== ctxItem)] : INTENT_ITEMS;
  let best: { item: IntentItem; a: HaveAsk; score: number } | undefined;
  for (const item of pool) for (const a of item.ask ?? []) {
    const have = new Set([...words(a.q), ...a.also, ...words(item.name)]);
    const hit = qw.filter((w) => have.has(w) || [...have].some((h) => h.length > 3 && w.startsWith(h.slice(0, h.length - 1)) && Math.abs(w.length - h.length) <= 2)).length;
    const score = hit / qw.length;
    const need = item === ctxItem ? 0.6 : 0.8;
    // A question about "delivered pastries under glass" must be about this item: its name or a close word has to be in it.
    const named = qw.some((w) => words(item.name).includes(w) || w.startsWith(item.say.split(" ")[0]!.slice(0, 6)) || (item.key === "croissant" && /^pastr/.test(w)));
    if (score >= need && (item === ctxItem || named) && (!best || score > best.score)) best = { item, a, score };
  }
  if (best) return toAnswer(best.item, best.a);
  if (ctxItem && qw.some((w) => HANDLING.has(w))) return toAnswer(ctxItem, { answer: ctxItem.rule, items: [{ title: "The rule", lines: [ctxItem.rule] }] });
  return undefined;
}
